import { useEffect, useState, useCallback } from 'react';
import {
  Crown, Loader2, AlertCircle, RefreshCw, Plus, Trash2, Pencil, X, Package,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import {
  listAdminPremium, grantPremium, revokePremium,
  listPremiumKits, createPremiumKit, updatePremiumKit, deletePremiumKit,
  type AdminPremium, type PremiumKit, type PremiumKitInput,
} from '@/shared/services/adminService';
import { useToast } from '@/shared/contexts/ToastContext';
import { DataTable, type DataTableColumn } from '@/shared/components/ui/DataTable';
import { RowActions } from '@/shared/components/ui/RowActions';
import { useTableSort } from '@/shared/hooks/useTableSort';

const PAGE_SIZE = 25;

type Tab = 'ativos' | 'kits';

const SERVICE_KIND_NAMES: Record<number, string> = {
  0: 'Free',
  1: 'Lendário',
  2: 'Mítico',
  3: 'Épico',
};

const premiumCols = (
  onRevoke: (userNum: number) => void,
  busyRevoke: number | null,
): DataTableColumn<AdminPremium>[] => [
  { key: 'userNum', label: 'UserNum', width: '80px', sortKey: 'userNum',
    cell: (p) => <span className="text-xs font-mono text-white/55">{p.userNum}</span> },
  { key: 'accountId', label: 'Conta', width: '1.5fr', sortKey: 'accountId',
    cell: (p) => <span className="text-sm font-bold text-white truncate">{p.accountId || '—'}</span> },
  { key: 'plano', label: 'Plano', width: '140px', sortKey: 'serviceKind',
    cell: (p) => <span className="text-xs text-amber-400">{SERVICE_KIND_NAMES[p.serviceKind ?? 0] || `SK ${p.serviceKind}`}</span> },
  { key: 'registerDate', label: 'Início', width: '180px', sortKey: 'registerDate',
    cell: (p) => <span className="text-[11px] text-white/55 font-mono">{p.registerDate ? new Date(p.registerDate).toLocaleString('pt-BR') : '—'}</span> },
  { key: 'expireDate', label: 'Expira', width: '180px', sortKey: 'expireDate',
    cell: (p) => <span className="text-[11px] text-white/85 font-mono">{p.expireDate ? new Date(p.expireDate).toLocaleString('pt-BR') : '—'}</span> },
  { key: 'actions', label: 'Ações', width: '60px', align: 'right', mobileHidden: true,
    cell: (p) => (
      <RowActions
        actions={[
          { label: 'Revogar', icon: <Trash2 size={13} />, onClick: () => onRevoke(p.userNum),
            variant: 'danger', disabled: busyRevoke === p.userNum },
        ]}
      />
    ) },
];

const premiumKitCols = (
  onEdit: (k: PremiumKit) => void,
  onDelete: (id: number) => void,
): DataTableColumn<PremiumKit>[] => [
  { key: 'id', label: 'ID', width: '60px',
    cell: (k) => <span className="text-xs font-mono text-white/55">{k.id}</span> },
  { key: 'title', label: 'Título', width: '2fr',
    cell: (k) => <span className="text-sm font-bold text-white truncate">{k.title || '—'}</span> },
  { key: 'priceCash', label: 'Preço Cash', width: '120px',
    cell: (k) => <span className="text-sm font-bold text-[#00e1d7]">{(k.priceCash ?? 0).toLocaleString('pt-BR')}</span> },
  { key: 'discount', label: 'Desc.', width: '80px',
    cell: (k) => (
      <span className={`text-xs font-bold ${(k.discount ?? 0) > 0 ? 'text-emerald-400' : 'text-white/40'}`}>
        {(k.discount ?? 0) > 0 ? `${k.discount}%` : '—'}
      </span>
    ) },
  { key: 'days', label: 'Dias', width: '100px',
    cell: (k) => <span className="text-sm text-amber-400 font-bold">{k.days ?? '—'}</span> },
  { key: 'kind', label: 'Tipo', width: '120px',
    cell: (k) => <span className="text-xs text-white/85">{SERVICE_KIND_NAMES[k.serviceKind ?? 0] || `SK ${k.serviceKind}`}</span> },
  { key: 'actions', label: 'Ações', width: '60px', align: 'right', mobileHidden: true,
    cell: (k) => (
      <RowActions
        actions={[
          { label: 'Editar', icon: <Pencil size={13} />, onClick: () => onEdit(k), variant: 'warning' },
          { label: 'Remover', icon: <Trash2 size={13} />, onClick: () => onDelete(k.id), variant: 'danger' },
        ]}
      />
    ) },
];

export default function PremiumPage() {
  const [tab, setTab] = useState<Tab>('ativos');

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
          <Crown className="text-amber-400" size={26} />
          Premium
        </h1>
      </div>

      <div className="flex gap-1 mb-5 surface-card rounded-xl p-1 w-fit overflow-x-auto">
        <TabBtn active={tab === 'ativos'} onClick={() => setTab('ativos')}>
          <Crown size={14} /> Ativos
        </TabBtn>
        <TabBtn active={tab === 'kits'} onClick={() => setTab('kits')}>
          <Package size={14} /> Planos
        </TabBtn>
      </div>

      {tab === 'ativos' ? <ActivesTab /> : <KitsTab />}
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer whitespace-nowrap ${
        active
          ? 'bg-[#00e1d7]/15 text-[#00e1d7]'
          : 'text-white/55 hover:text-white hover:bg-white/[0.03]'
      }`}
    >
      {children}
    </button>
  );
}

/* ======================================================== ACTIVES ======== */

function ActivesTab() {
  const { notify } = useToast();
  const [items, setItems] = useState<AdminPremium[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showGrant, setShowGrant] = useState(false);
  const [grantUserNum, setGrantUserNum] = useState('');
  const [grantServiceKind, setGrantServiceKind] = useState(1);
  const [grantDays, setGrantDays] = useState('30');
  const [busyGrant, setBusyGrant] = useState(false);
  const [busyRevoke, setBusyRevoke] = useState<number | null>(null);
  const { sorted, sort, toggle } = useTableSort(items, { key: 'expireDate', order: 'desc' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listAdminPremium({ skip: page * PAGE_SIZE, limit: PAGE_SIZE });
      setItems(list.items);
      setTotal(list.total);
    } catch (err: any) {
      setError(err?.detail || err?.message || 'Erro');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGrant = async () => {
    const un = parseInt(grantUserNum);
    if (!un) { notify('error', 'UserNum inválido'); return; }
    const days = parseInt(grantDays);
    if (!days || days <= 0) { notify('error', 'Dias inválido'); return; }
    setBusyGrant(true);
    try {
      const res = await grantPremium(un, days, { type: 1, serviceKind: grantServiceKind });
      notify('success', `${SERVICE_KIND_NAMES[grantServiceKind] || 'Premium'} · ${days} dias · ${res.message}`);
      setGrantUserNum('');
      setShowGrant(false);
      await fetchData();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally { setBusyGrant(false); }
  };

  const handleRevoke = async (userNum: number) => {
    if (!confirm(`Revogar premium do usuário ${userNum}?`)) return;
    setBusyRevoke(userNum);
    try {
      const res = await revokePremium(userNum);
      notify('success', res.message);
      await fetchData();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally { setBusyRevoke(null); }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-white/65">{total.toLocaleString('pt-BR')} premium(s) ativos</p>
        <div className="flex gap-2">
          <button onClick={fetchData} disabled={loading}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                       bg-white/[0.04] hover:bg-white/[0.08] text-white/75 transition cursor-pointer">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
          <button onClick={() => setShowGrant((s) => !s)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold
                       btn-primary transition cursor-pointer">
            <Plus size={14} /> Conceder Premium
          </button>
        </div>
      </div>

      {showGrant && (
        <div className="surface-card rounded-xl p-5 mb-5">
          <h3 className="text-sm font-bold text-white mb-1">Conceder Premium</h3>
          <p className="text-[11px] text-white/45 mb-3">
            Sobrescreve o premium atual da conta (não soma) — expira em AGORA + dias.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="text-[11px] text-white/55 mb-1 block">UserNum</label>
              <input type="text" inputMode="numeric"
                value={grantUserNum}
                onChange={(e) => setGrantUserNum(e.target.value.replace(/\D/g, ''))}
                placeholder="ex: 4360"
                className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="text-[11px] text-white/55 mb-1 block">Plano (ServiceKind)</label>
              <select
                value={grantServiceKind}
                onChange={(e) => setGrantServiceKind(parseInt(e.target.value))}
                className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white">
                <option value={1}>{SERVICE_KIND_NAMES[1]}</option>
                <option value={2}>{SERVICE_KIND_NAMES[2]}</option>
                <option value={3}>{SERVICE_KIND_NAMES[3]}</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] text-white/55 mb-1 block">Dias</label>
              <input type="text" inputMode="numeric"
                value={grantDays}
                onChange={(e) => setGrantDays(e.target.value.replace(/\D/g, ''))}
                placeholder="30"
                className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white" />
            </div>
            <div className="flex items-end">
              <button onClick={handleGrant} disabled={busyGrant || !grantUserNum || !grantDays}
                className="w-full px-4 py-2 rounded-lg btn-primary
                           font-bold text-sm transition cursor-pointer disabled:opacity-50
                           flex items-center justify-center gap-2">
                {busyGrant ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                Conceder
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="surface-card rounded-xl p-4 mb-4 flex items-start gap-3 border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      <DataTable<AdminPremium>
        columns={premiumCols(handleRevoke, busyRevoke)}
        rows={sorted}
        sort={sort}
        onSort={toggle}
        rowKey={(p) => `${p.userNum}-${p.expireDate}`}
        loading={loading}
        empty="Nenhum premium ativo"
        mobileTitle={(p) => (
          <span className="flex items-center gap-2">
            {p.accountId || '—'} <span className="text-[10px] text-white/45 font-mono">#{p.userNum}</span>
          </span>
        )}
        mobileSubtitle={(p) => SERVICE_KIND_NAMES[p.serviceKind ?? 0] || `SK ${p.serviceKind}`}
        mobileActions={(p) => (
          <RowActions
            actions={[
              { label: 'Revogar', icon: <Trash2 size={13} />, onClick: () => handleRevoke(p.userNum),
                variant: 'danger', disabled: busyRevoke === p.userNum },
            ]}
            align="left"
            
          />
        )}
      />

      <div className="flex items-center justify-between mt-4 text-xs text-white/55">
        <span>Página {page + 1} de {totalPages}</span>
        <div className="flex gap-2">
          <button onClick={() => page > 0 && setPage(page - 1)} disabled={page === 0 || loading}
            className="px-3 py-1.5 rounded-lg bg-[#161e26] border border-white/[0.06]
                       hover:border-amber-500/40 disabled:opacity-40 disabled:cursor-not-allowed
                       transition cursor-pointer flex items-center gap-1">
            <ChevronLeft size={14} /> Anterior
          </button>
          <button onClick={() => page + 1 < totalPages && setPage(page + 1)} disabled={page + 1 >= totalPages || loading}
            className="px-3 py-1.5 rounded-lg bg-[#161e26] border border-white/[0.06]
                       hover:border-amber-500/40 disabled:opacity-40 disabled:cursor-not-allowed
                       transition cursor-pointer flex items-center gap-1">
            Próxima <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ======================================================== KITS =========== */

function KitsTab() {
  const { notify } = useToast();
  const [items, setItems] = useState<PremiumKit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<PremiumKit | null>(null);
  const [creating, setCreating] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try { setItems(await listPremiumKits()); }
    catch (err: any) { setError(err?.detail || err?.message || 'Erro'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id: number) => {
    if (!confirm('Deletar esse plano?')) return;
    try {
      await deletePremiumKit(id);
      notify('success', 'Deletado');
      await fetchData();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-white/65">{items.length} plano(s) · CabalCash.web_cash_services_premium</p>
        <button onClick={() => setCreating(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold
                     btn-primary transition cursor-pointer">
          <Plus size={14} /> Novo Plano
        </button>
      </div>

      {error && (
        <div className="surface-card rounded-xl p-4 mb-4 flex items-start gap-3 border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      <DataTable<PremiumKit>
        columns={premiumKitCols(setEditing, handleDelete)}
        rows={items}
        rowKey={(k) => k.id}
        loading={loading}
        empty="Nenhum plano cadastrado"
        mobileTitle={(k) => (
          <span className="flex items-center gap-2">{k.title || '—'} <span className="text-[10px] text-white/45 font-mono">#{k.id}</span></span>
        )}
        mobileSubtitle={(k) => `${(k.priceCash ?? 0).toLocaleString('pt-BR')} Cash · ${k.days ?? '—'} dias · ${SERVICE_KIND_NAMES[k.serviceKind ?? 0] || `SK ${k.serviceKind}`}`}
        mobileActions={(k) => (
          <RowActions
            actions={[
              { label: 'Editar', icon: <Pencil size={13} />, onClick: () => setEditing(k), variant: 'warning' },
              { label: 'Remover', icon: <Trash2 size={13} />, onClick: () => handleDelete(k.id), variant: 'danger' },
            ]}
            align="left"
            
          />
        )}
      />

      {(editing || creating) && (
        <KitEditor kit={editing} onClose={() => { setEditing(null); setCreating(false); }}
                   onSaved={async () => { await fetchData(); setEditing(null); setCreating(false); }} />
      )}
    </div>
  );
}

function KitEditor({ kit, onClose, onSaved }: { kit: PremiumKit | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const { notify } = useToast();
  const [title, setTitle] = useState(kit?.title || '');
  const [priceCash, setPriceCash] = useState(String(kit?.priceCash ?? 0));
  const [days, setDays] = useState(String(kit?.days ?? 30));
  const [serviceKind, setServiceKind] = useState(kit?.serviceKind ?? 1);
  const [discount, setDiscount] = useState(String(kit?.discount ?? 0));
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload: PremiumKitInput = {
        title: title || undefined,
        priceCash: parseInt(priceCash) || 0,
        days: parseInt(days) || 30,
        serviceKind,
        discount: Math.min(100, Math.max(0, parseInt(discount) || 0)),
      };
      if (kit) await updatePremiumKit(kit.id, payload);
      else await createPremiumKit(payload);
      notify('success', kit ? 'Plano atualizado' : 'Plano criado');
      await onSaved();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="surface-card rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white">{kit ? 'Editar' : 'Novo'} Plano Premium</h2>
          <button onClick={onClose} className="text-white/55 hover:text-white"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] text-white/55 mb-1 block">Título</label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="ex: Plano Lendário 30 dias"
              className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-white/55 mb-1 block">Preço Cash</label>
              <input type="text" inputMode="numeric" value={priceCash}
                onChange={(e) => setPriceCash(e.target.value.replace(/\D/g, ''))}
                className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="text-[11px] text-white/55 mb-1 block">Dias</label>
              <input type="text" inputMode="numeric" value={days}
                onChange={(e) => setDays(e.target.value.replace(/\D/g, ''))}
                className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-white/55 mb-1 block">Tipo (ServiceKind)</label>
              <select value={serviceKind} onChange={(e) => setServiceKind(parseInt(e.target.value))}
                className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white">
                <option value={1}>1 — Lendário</option>
                <option value={2}>2 — Mítico</option>
                <option value={3}>3 — Épico</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] text-white/55 mb-1 block">Desconto (%)</label>
              <input type="text" inputMode="numeric" value={discount}
                onChange={(e) => setDiscount(e.target.value.replace(/\D/g, '').slice(0, 3))}
                placeholder="0"
                className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white" />
              <p className="text-[10px] text-white/40 mt-1">0–100 · risca preço original na venda</p>
            </div>
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-lg bg-white/[0.04] text-white/75 hover:bg-white/[0.08] transition cursor-pointer text-sm">
            Cancelar
          </button>
          <button onClick={save} disabled={busy}
            className="flex-1 px-4 py-2.5 rounded-lg btn-primary
                       font-bold text-sm transition cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2">
            {busy && <Loader2 size={14} className="animate-spin" />}
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}
